import React from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';

import { Button } from 'shared/components/Button';
import { useCheckoutContext } from 'checkout/providers/CheckoutContextProvider';
import { GiftCard } from 'checkout/components/Payment/GiftCard';
import { t } from 'localization/utils/t';
import { getRedirectionPath } from 'localization/utils/getRedirectionPath';

const TP = 'checkout.components.Payment.Afterpay';

declare global {
  interface Window {
    presentAfterpay: any;
  }
}

export const Afterpay = () => {
  const { redirectToAfterpay, displayGlobalError } = useCheckoutContext();

  const handleAfterpayInfoClick = () => {
    const infoElement = document.querySelector('#afterpay-info-icon');

    if (!infoElement) {
      displayGlobalError(t(`${TP}.missingAfterPayInfo`, 'Cannot display Afterpay learn more.'));
      return;
    }

    const config = {
      cssSelector: '#afterpay-info-icon',
      locale: 'en-US',
      currency: 'USD',
      afterpayLogoColor: 'black',
      minMaxThreshold: {
        min: 3500,
        max: 100000,
      },
    };

    if (window?.presentAfterpay) {
      // eslint-disable-next-line new-cap
      new window.presentAfterpay(config).modal.open();
    }
  };

  return (
    <Afterpay.Wrapper>
      <GiftCard />
      <p>
        {t(`${TP}.afterpayPlacement`, '4 interest-free payments')}
        <Afterpay.Info
          data-qa="afterpay_info_icon"
          id="afterpay-info-icon"
          onClick={handleAfterpayInfoClick}
        >
          ⓘ
        </Afterpay.Info>
      </p>
      <p>
        {t(
          `${TP}.afterpayInstructions`,
          'You will be redirected to Afterpay to complete your purchase securely.',
        )}
      </p>
      <p>
        <Trans
          i18nKey={`${TP}.afterpayTerms`}
          defaults="By proceeding, you confirm you have read and agree to the <br> <purchasePolicyLink>Purchases and Returns Policy</purchasePolicyLink> and Afterpay's <afterpayInstallmentAgreementLink>Installment Agreement</afterpayInstallmentAgreementLink>."
          components={{
            afterpayInstallmentAgreementLink: (
              <Afterpay.Link
                href="https://www.afterpay.com/en-US/installment-agreement"
                target="_blank"
                rel="noreferrer"
              />
            ),
            purchasePolicyLink: (
              <Afterpay.Link
                href={getRedirectionPath('/returns')}
                target="_blank"
                rel="noreferrer"
              />
            ),
          }}
        />
      </p>
      <Button data-qa="afterpay-submit-button" onClick={redirectToAfterpay}>
        {t(`${TP}.continueAfterpayButton`, 'Place Order with Afterpay')}
      </Button>
    </Afterpay.Wrapper>
  );
};

Afterpay.Wrapper = styled.div`
  width: 100%;
  p {
    margin-bottom: 16px;
  }
  a {
    text-decoration: underline;
  }
  button {
    margin-top: 48px;
  }
`;

Afterpay.Link = styled.a`
  text-decoration: underline;
`;

Afterpay.Image = styled.img`
  width: 82px;
  margin-bottom: 12px;
`;

Afterpay.Info = styled.span`
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.black};
  margin-left: 5px;
`;
